import { PageTitle, H2Heading, H3Heading } from "../../../components/Headers";
import { PageBody } from "../../../components/PageBody";
import { Helmet } from "react-helmet-async";
import { ArticleParagraph } from "../../../components/Paragraphs";
import { Link } from "react-router-dom";

import {
    COURSE_PRESETS,
    HANDICAP_SCORING_PROFILES,
    SCORE_TYPES,
    SCORE_LABELS_SHORT,
    SCORE_COLOURS
} from "./utility/golfConfig";
import { calculateCourseHandicap, getScoringProbabilities } from "./utility/golfSimulation";


/**
 * Format probability as percentage
 */
const formatPct = (probability) => {
    const pct = probability * 100;
    if (pct < 1 && pct > 0) return pct.toFixed(2);
    if (pct < 10) return pct.toFixed(1);
    return Math.round(pct).toString();
};


/**
 * Interactive course handicap calculator
 */
const CourseHandicapCalculator = () => {
    const examples = [
        { handicap: 10, course: COURSE_PRESETS.easy },
        { handicap: 10, course: COURSE_PRESETS.medium },
        { handicap: 10, course: COURSE_PRESETS.hard },
        { handicap: 20, course: COURSE_PRESETS.easy },
        { handicap: 20, course: COURSE_PRESETS.medium },
        { handicap: 20, course: COURSE_PRESETS.hard },
    ];

    return (
        <div className="overflow-x-auto">
            <table className="min-w-full text-sm border-collapse">
                <thead>
                    <tr className="bg-gray-100">
                        <th className="px-3 py-2 text-left border">Handicap Index</th>
                        <th className="px-3 py-2 text-left border">Course</th>
                        <th className="px-3 py-2 text-center border">Slope</th>
                        <th className="px-3 py-2 text-center border">Rating</th>
                        <th className="px-3 py-2 text-center border">Course Handicap</th>
                        <th className="px-3 py-2 text-center border">Expected Score</th>
                    </tr>
                </thead>
                <tbody>
                    {examples.map((ex, i) => {
                        const courseHcp = calculateCourseHandicap(
                            ex.handicap,
                            ex.course.slopeRating,
                            ex.course.courseRating,
                            ex.course.par
                        );
                        const expected = ex.course.par + courseHcp;

                        return (
                            <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                <td className="px-3 py-2 border font-medium">{ex.handicap}</td>
                                <td className="px-3 py-2 border">{ex.course.name}</td>
                                <td className="px-3 py-2 border text-center">{ex.course.slopeRating}</td>
                                <td className="px-3 py-2 border text-center">{ex.course.courseRating}</td>
                                <td className="px-3 py-2 border text-center font-semibold">{courseHcp}</td>
                                <td className="px-3 py-2 border text-center font-bold text-green-700">
                                    {expected} (+{courseHcp})
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};


/**
 * Slope impact visualisation
 */
const SlopeImpactChart = () => {
    const slopes = [90, 100, 113, 125, 140, 155];
    const handicaps = [5, 10, 15, 20, 25];
    const baseCourse = { par: 72, courseRating: 72.0 };

    return (
        <div className="overflow-x-auto">
            <table className="min-w-full text-sm border-collapse">
                <thead>
                    <tr className="bg-gray-100">
                        <th className="px-3 py-2 text-left border">Handicap</th>
                        {slopes.map(slope => (
                            <th key={slope} className="px-3 py-2 text-center border">
                                Slope {slope}
                                {slope === 113 && <span className="text-xs text-gray-500 block">(avg)</span>}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {handicaps.map((hcp, i) => (
                        <tr key={hcp} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-3 py-2 border font-medium">{hcp}</td>
                            {slopes.map(slope => {
                                const courseHcp = calculateCourseHandicap(
                                    hcp,
                                    slope,
                                    baseCourse.courseRating,
                                    baseCourse.par
                                );
                                const expected = baseCourse.par + courseHcp;
                                const diff = courseHcp - hcp;
                                const diffText = diff > 0 ? `+${diff}` : diff === 0 ? '0' : diff.toString();

                                return (
                                    <td
                                        key={slope}
                                        className="px-3 py-2 border text-center"
                                        style={{
                                            backgroundColor: slope > 113
                                                ? `rgba(239, 68, 68, ${(slope - 113) / 80})`
                                                : slope < 113
                                                    ? `rgba(34, 197, 94, ${(113 - slope) / 50})`
                                                    : 'transparent'
                                        }}
                                    >
                                        <div className="font-semibold">{expected}</div>
                                        <div className="text-xs text-gray-500">({diffText} from index)</div>
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};


/**
 * Scoring probability table by handicap
 */
const ScoringProbabilityTable = () => {
    const handicaps = [0, 5, 10, 15, 20, 25];
    const scoreTypes = [
        SCORE_TYPES.EAGLE_OR_BETTER,
        SCORE_TYPES.BIRDIE,
        SCORE_TYPES.PAR,
        SCORE_TYPES.BOGEY,
        SCORE_TYPES.DOUBLE_BOGEY,
        SCORE_TYPES.TRIPLE_OR_WORSE
    ];

    return (
        <div className="overflow-x-auto">
            <table className="min-w-full text-sm border-collapse">
                <thead>
                    <tr className="bg-gray-100">
                        <th className="px-3 py-2 text-left border">Score Type</th>
                        {handicaps.map(hcp => (
                            <th key={hcp} className="px-3 py-2 text-center border">
                                Hcp {hcp}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {scoreTypes.map((scoreType, i) => (
                        <tr key={scoreType} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td
                                className="px-3 py-2 border font-medium"
                                style={{
                                    backgroundColor: SCORE_COLOURS[scoreType],
                                    color: scoreType === SCORE_TYPES.DOUBLE_BOGEY ||
                                           scoreType === SCORE_TYPES.TRIPLE_OR_WORSE
                                        ? '#ffffff'
                                        : '#000000'
                                }}
                            >
                                {SCORE_LABELS_SHORT[scoreType]}
                            </td>
                            {handicaps.map(hcp => {
                                const prob = HANDICAP_SCORING_PROFILES[hcp][scoreType];
                                const perRound = prob * 18;

                                return (
                                    <td key={hcp} className="px-3 py-2 border text-center">
                                        <div>{formatPct(prob)}%</div>
                                        <div className="text-xs text-gray-500">~{perRound.toFixed(1)}/round</div>
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                    <tr className="bg-gray-200 font-semibold">
                        <td className="px-3 py-2 border">Expected Score</td>
                        {handicaps.map(hcp => {
                            const probs = HANDICAP_SCORING_PROFILES[hcp];
                            const expectedPerHole =
                                probs[SCORE_TYPES.EAGLE_OR_BETTER] * -2 +
                                probs[SCORE_TYPES.BIRDIE] * -1 +
                                probs[SCORE_TYPES.PAR] * 0 +
                                probs[SCORE_TYPES.BOGEY] * 1 +
                                probs[SCORE_TYPES.DOUBLE_BOGEY] * 2 +
                                probs[SCORE_TYPES.TRIPLE_OR_WORSE] * 3;
                            const expectedScore = 72 + Math.round(expectedPerHole * 18);

                            return (
                                <td key={hcp} className="px-3 py-2 border text-center text-green-700">
                                    ~{expectedScore}
                                </td>
                            );
                        })}
                    </tr>
                </tbody>
            </table>
        </div>
    );
};


/**
 * Hole type probability comparison
 */
const HoleTypeProbabilityTable = () => {
    const handicap = 10;
    const holeTypes = [3, 4, 5];
    const scoreTypes = [
        SCORE_TYPES.BIRDIE,
        SCORE_TYPES.PAR,
        SCORE_TYPES.BOGEY,
        SCORE_TYPES.DOUBLE_BOGEY
    ];

    return (
        <div className="overflow-x-auto">
            <table className="min-w-full text-sm border-collapse">
                <thead>
                    <tr className="bg-gray-100">
                        <th className="px-3 py-2 text-left border">Score Type</th>
                        {holeTypes.map(par => (
                            <th key={par} className="px-3 py-2 text-center border">
                                Par {par}
                            </th>
                        ))}
                        <th className="px-3 py-2 text-center border">Why?</th>
                    </tr>
                </thead>
                <tbody>
                    {scoreTypes.map((scoreType, i) => {
                        const probs = holeTypes.map(par =>
                            getScoringProbabilities(handicap, par, 0.25)[scoreType]
                        );
                        const maxIdx = probs.indexOf(Math.max(...probs));
                        const minIdx = probs.indexOf(Math.min(...probs));

                        const explanations = {
                            [SCORE_TYPES.BIRDIE]: 'Par 5s offer reachable-in-two opportunities',
                            [SCORE_TYPES.PAR]: 'Par 4s are the baseline; par 5s slightly easier',
                            [SCORE_TYPES.BOGEY]: 'Par 3s require precision with longer clubs',
                            [SCORE_TYPES.DOUBLE_BOGEY]: 'Par 3s have less margin for recovery'
                        };

                        return (
                            <tr key={scoreType} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                <td
                                    className="px-3 py-2 border font-medium"
                                    style={{ backgroundColor: SCORE_COLOURS[scoreType] }}
                                >
                                    {SCORE_LABELS_SHORT[scoreType]}
                                </td>
                                {probs.map((prob, idx) => (
                                    <td
                                        key={holeTypes[idx]}
                                        className={`px-3 py-2 border text-center ${
                                            idx === maxIdx ? 'font-bold text-green-700' :
                                            idx === minIdx ? 'text-red-600' : ''
                                        }`}
                                    >
                                        {formatPct(prob)}%
                                    </td>
                                ))}
                                <td className="px-3 py-2 border text-xs text-gray-600">
                                    {explanations[scoreType]}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            <p className="text-xs text-gray-500 mt-2">
                Probabilities shown for a 10-handicap golfer with average consistency
            </p>
        </div>
    );
};


/**
 * Formula explanation component
 */
const FormulaExplanation = ({ formula, variables }) => {
    return (
        <div className="bg-gray-50 rounded-lg p-4 my-4">
            <div className="text-center font-mono text-lg mb-4 p-3 bg-white rounded border">
                {formula}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                {variables.map((v, i) => (
                    <div key={i} className="flex gap-2">
                        <span className="font-mono font-semibold text-green-700">{v.symbol}:</span>
                        <span className="text-gray-700">{v.description}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};


/**
 * Info box component
 */
const InfoBox = ({ title, children, type = 'info' }) => {
    const colours = {
        info: 'bg-blue-50 border-blue-200 text-blue-800',
        warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
        success: 'bg-green-50 border-green-200 text-green-800'
    };

    return (
        <div className={`rounded-lg border p-4 my-4 ${colours[type]}`}>
            {title && <div className="font-semibold mb-2">{title}</div>}
            <div className="text-sm">{children}</div>
        </div>
    );
};


export const GolfMethodology = () => {
    return (
        <PageBody>
            <Helmet>
                <title>Golf Simulation Methodology | VizBadger</title>
                <meta
                    name="description"
                    content="Understand how the golf round simulation works, including slope rating, course handicap calculation, and hole-by-hole scoring probabilities."
                />
                <meta name="keywords" content="Golf handicap, slope rating, course rating, USGA formula, golf simulation methodology, scoring probabilities" />
            </Helmet>

            <PageTitle title="How The Golf Simulation Works" />

            <ArticleParagraph
                lines={[
                    "This page explains the methodology behind the golf round simulation, including the handicap system, how slope affects scoring, and the probability model used for hole-by-hole simulation."
                ]}
            />

            <div className="mb-6">
                <Link
                    to="/sports-models/golf/round-prediction"
                    className="inline-flex items-center text-green-600 hover:text-green-800"
                >
                    &larr; Back to Golf Simulation
                </Link>
            </div>

            {/* Section 0: Modelling Approach */}
            <H2Heading>The Modelling Approach</H2Heading>

            <ArticleParagraph
                lines={[
                    "Unlike other sports simulations on this site (such as the pool simulation, which creates bespoke observable attributes that could be populated from watching real games), the golf simulation leverages an existing universal measurement system: the handicap.",
                    "The handicap system already provides a standardised way to quantify golfer ability, and the slope/course rating system quantifies course difficulty. Rather than creating custom parameters like 'putting accuracy' or 'driving consistency', we use handicap as the skill proxy and slope as the difficulty proxy."
                ]}
            />

            <InfoBox title="How The Probabilities Are Derived" type="info">
                <p className="mb-2">
                    The scoring probabilities in this simulation are <strong>calibrated to match published statistics</strong>, not computed through statistical regression on raw data.
                </p>
                <p className="mb-2">
                    The process works like this: published research tells us that a 10-handicap golfer averages approximately 0.7 birdies, 7 pars, and 7.7 bogeys per round. We then manually calibrated probability values that, when sampled 18 times (once per hole), produce approximately those outcomes.
                </p>
                <p>
                    Each handicap bracket was fitted independently to match its target statistics. The probabilities are not mathematically derived from each other through a formula—they are separately calibrated to reproduce observed scoring patterns.
                </p>
            </InfoBox>

            <ArticleParagraph
                lines={[
                    "This approach means the simulation produces realistic scoring distributions that match what golfers at each handicap level actually score. The official USGA course handicap formula then adjusts these probabilities based on course difficulty, creating a complete model of how handicap and course interact."
                ]}
            />

            {/* Section 1: The Handicap System */}
            <H2Heading>The Handicap System</H2Heading>

            <ArticleParagraph
                lines={[
                    "The handicap system allows golfers of different abilities to compete fairly. Your Handicap Index is a portable number that represents your potential ability, calculated from your best scores.",
                    "However, the same golfer will score differently on different courses. A handicap 10 player might shoot 82 on an easy course but 88 on a championship course. This is where slope comes in."
                ]}
            />

            <InfoBox title="Handicap Index Ranges" type="info">
                <ul className="list-disc list-inside space-y-1">
                    <li><strong>0 (Scratch):</strong> Plays to par on an average course</li>
                    <li><strong>1-9 (Single Figure):</strong> Strong amateur players</li>
                    <li><strong>10-18 (Mid):</strong> Average club golfers</li>
                    <li><strong>19-36 (High):</strong> Beginners and casual players</li>
                </ul>
            </InfoBox>

            {/* Section 2: What is Slope? */}
            <H2Heading>What Is Slope Rating?</H2Heading>

            <ArticleParagraph
                lines={[
                    "Slope Rating measures how much harder a course is for a bogey golfer (around 20 handicap) compared to a scratch golfer. It answers: 'How much does difficulty increase as handicap increases?'",
                    "A course with a high slope has features that disproportionately penalise higher-handicap players: long carries over hazards, narrow fairways, thick rough, and fast greens."
                ]}
            />

            <InfoBox title="Slope Rating Ranges" type="info">
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <strong>Typical Range:</strong> 55 to 155
                        <ul className="list-disc list-inside mt-1 text-xs">
                            <li>55: Very easy, wide open</li>
                            <li>113: Average difficulty</li>
                            <li>155: Extremely difficult</li>
                        </ul>
                    </div>
                    <div>
                        <strong>Common Values:</strong>
                        <ul className="list-disc list-inside mt-1 text-xs">
                            <li>Municipal course: 105-115</li>
                            <li>Private club: 120-135</li>
                            <li>Championship: 135-150</li>
                        </ul>
                    </div>
                </div>
            </InfoBox>

            <ArticleParagraph
                lines={[
                    "Slope is always paired with Course Rating, which is the expected score for a scratch golfer. Together, these two numbers define how difficult a course is for players of all abilities."
                ]}
            />

            {/* Section 3: The Course Handicap Formula */}
            <H2Heading>The Course Handicap Formula</H2Heading>

            <p className="mb-4 text-gray-700">
                Your Course Handicap is calculated using the{' '}
                <a
                    href="https://www.usga.org/content/usga/home-page/handicapping/roh/Content/rules/6%201%20Course%20Handicap%20Calculation.htm"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-600 hover:underline"
                >
                    official USGA formula
                </a>
                . This adjusts your portable Handicap Index for the specific course you're playing.
            </p>

            <FormulaExplanation
                formula="Course Handicap = Handicap Index × (Slope ÷ 113) + (Course Rating − Par)"
                variables={[
                    { symbol: 'Handicap Index', description: 'Your portable handicap (0-54)' },
                    { symbol: 'Slope', description: 'Course slope rating (55-155)' },
                    { symbol: '113', description: 'Standard slope (average course)' },
                    { symbol: 'Course Rating', description: 'Expected scratch score' },
                    { symbol: 'Par', description: 'Course par (typically 72)' }
                ]}
            />

            <ArticleParagraph
                lines={[
                    "The formula has two parts: the slope adjustment and the rating differential. The slope part scales your handicap up or down. The rating part adds strokes if the course plays harder than par for scratch golfers."
                ]}
            />

            <H3Heading>How Slope Affects Expected Score</H3Heading>

            <ArticleParagraph
                lines={[
                    "The table below shows how the same golfer's expected score changes across different slope ratings. Notice how higher handicaps are affected more dramatically by slope changes."
                ]}
            />

            <SlopeImpactChart />

            <InfoBox type="warning">
                A 20-handicap golfer gains 7 strokes moving from slope 90 to slope 155.
                A 5-handicap golfer only gains 2 strokes on the same change.
                This is the key insight: slope measures <em>relative</em> difficulty.
            </InfoBox>

            {/* Section 4: Course Handicap Examples */}
            <H2Heading>Course Handicap Examples</H2Heading>

            <ArticleParagraph
                lines={[
                    "Here's how the formula works in practice for different handicaps across our preset courses."
                ]}
            />

            <CourseHandicapCalculator />

            {/* Section 5: Scoring Probabilities */}
            <H2Heading>Scoring Probability Model</H2Heading>

            <ArticleParagraph
                lines={[
                    "Rather than using a simple normal distribution around the expected score, our simulation models each hole independently. This produces more realistic scoring patterns with the occasional eagle and the inevitable blow-up hole."
                ]}
            />

            <H3Heading>Base Probabilities by Handicap</H3Heading>

            <ArticleParagraph
                lines={[
                    "These probabilities are derived from published golf statistics analysing thousands of rounds across different handicap levels. They represent the baseline probability of each score type on an average hole."
                ]}
            />

            <ScoringProbabilityTable />

            <InfoBox title="Data Sources" type="info">
                The scoring probabilities are derived from published research analysing millions of rounds:
                <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>
                        <a href="https://mygolfspy.com/labs/study-overall-golfer-performance-by-handicap/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            MyGolfSpy: Overall Golfer Performance By Handicap
                        </a>
                        {' '}- Comprehensive scoring breakdown by handicap level
                    </li>
                    <li>
                        <a href="https://golfinsideruk.com/birdies-bogey-doubles-by-handicap/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            Golf Insider: Birdies, Bogeys & Doubles By Handicap
                        </a>
                        {' '}- Detailed per-round scoring statistics
                    </li>
                    <li>
                        <a href="https://www.arccosgolf.com/blogs/community/what-really-separates-a-5-handicap-from-a-10" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            Arccos Golf: What Separates a 5 from a 10 Handicap
                        </a>
                        {' '}- Data from millions of tracked shots
                    </li>
                    <li>
                        <a href="https://practical-golf.com/shotscope-handicap-data" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            Practical Golf: Shot Scope Handicap Data Analysis
                        </a>
                        {' '}- Aggregated statistics from Shot Scope users
                    </li>
                </ul>
                <p className="mt-2 text-xs">Values are interpolated for handicaps between the brackets shown.</p>
            </InfoBox>

            {/* Section 6: Hole Type Adjustments */}
            <H2Heading>How Hole Type Affects Scoring</H2Heading>

            <ArticleParagraph
                lines={[
                    "Not all holes are created equal. Par 3s, 4s, and 5s each have different scoring characteristics. We apply adjustments to the base probabilities based on hole type."
                ]}
            />

            <HoleTypeProbabilityTable />

            <H3Heading>Why These Adjustments?</H3Heading>

            <div className="space-y-4 text-sm">
                <div className="bg-gray-50 rounded-lg p-3">
                    <strong className="text-green-700">Par 3s are slightly harder:</strong>
                    <p className="mt-1 text-gray-700">
                        Golfers must hit a long iron or wood directly to the green. There's no fairway landing zone,
                        and missing the green often means a difficult chip or bunker shot. The lack of a second
                        chance makes bogeys more common.
                    </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-3">
                    <strong className="text-green-700">Par 4s are the baseline:</strong>
                    <p className="mt-1 text-gray-700">
                        With 10 par 4s on a typical course, these holes represent the standard against which
                        other holes are measured. The drive-approach-putt rhythm is what handicaps are built around.
                    </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-3">
                    <strong className="text-green-700">Par 5s offer more scoring opportunities:</strong>
                    <p className="mt-1 text-gray-700">
                        The extra shot provides margin for error. Even after a poor drive, golfers can
                        recover and still reach the green in regulation. Longer hitters can reach in two,
                        creating eagle chances. Par 5s typically yield the most birdies.
                    </p>
                </div>
            </div>

            {/* Section 7: The Simulation Process */}
            <H2Heading>How The Simulation Works</H2Heading>

            <ArticleParagraph
                lines={[
                    "Here's the step-by-step process for simulating a round:"
                ]}
            />

            <ol className="list-decimal list-inside space-y-3 text-sm my-4">
                <li>
                    <strong>Calculate Course Handicap</strong> using the USGA formula
                </li>
                <li>
                    <strong>For each hole (1-18):</strong>
                    <ul className="list-disc list-inside ml-6 mt-1 text-gray-700">
                        <li>Get base scoring probabilities for the golfer's handicap (interpolated between brackets)</li>
                        <li>Apply hole type adjustments based on par (3, 4, or 5)</li>
                        <li>Apply consistency modifier (more consistent = more pars, fewer extremes)</li>
                        <li>Normalise probabilities to sum to 100%</li>
                        <li>Generate random number and select score type based on cumulative probability</li>
                        <li>Record hole score (par + strokes relative to par)</li>
                    </ul>
                </li>
                <li>
                    <strong>Aggregate results:</strong> total score, front/back nine, scoring breakdown
                </li>
            </ol>

            <InfoBox title="Why Hole-by-Hole?" type="success">
                <p>
                    Normal distribution models assume scores cluster symmetrically around the mean.
                    Real golf doesn't work this way: a player might have 14 pars, 3 bogeys, and 1 triple bogey.
                    Hole-by-hole simulation captures this asymmetry and produces realistic scorecards.
                </p>
            </InfoBox>

            {/* Section 8: Consistency */}
            <H2Heading>The Consistency Factor</H2Heading>

            <ArticleParagraph
                lines={[
                    "Some golfers are steady: they rarely have a blow-up hole but also rarely make birdies. Others are volatile: capable of brilliance but prone to disasters. The consistency parameter models this.",
                    "Technically, consistency adjusts the probability distribution towards or away from par. High consistency increases par probability and reduces the extremes (eagles and triples). Low consistency does the opposite."
                ]}
            />

            <div className="grid grid-cols-2 gap-4 text-sm my-4">
                <div className="bg-green-50 rounded-lg p-3">
                    <strong className="text-green-800">Consistent Player (0.15-0.20)</strong>
                    <ul className="list-disc list-inside mt-2 text-gray-700">
                        <li>Fewer birdies</li>
                        <li>More pars</li>
                        <li>Fewer double bogeys</li>
                        <li>Narrower score range</li>
                    </ul>
                </div>
                <div className="bg-red-50 rounded-lg p-3">
                    <strong className="text-red-800">Erratic Player (0.30-0.35)</strong>
                    <ul className="list-disc list-inside mt-2 text-gray-700">
                        <li>More birdies</li>
                        <li>Fewer pars</li>
                        <li>More blow-up holes</li>
                        <li>Wider score range</li>
                    </ul>
                </div>
            </div>

            {/* Section 9: Limitations */}
            <H2Heading>Model Limitations</H2Heading>

            <ArticleParagraph
                lines={[
                    "This simulation is a simplified model. Real golf involves many factors we don't capture:"
                ]}
            />

            <ul className="list-disc list-inside space-y-2 text-sm my-4">
                <li><strong>Weather:</strong> Wind, rain, and temperature significantly affect scoring</li>
                <li><strong>Course conditions:</strong> Firm vs soft, rough height, green speed</li>
                <li><strong>Individual hole characteristics:</strong> Not all par 4s are equal</li>
                <li><strong>Player strengths/weaknesses:</strong> Some players excel on par 5s, others on par 3s</li>
                <li><strong>Mental factors:</strong> Pressure, fatigue, course familiarity</li>
                <li><strong>Sequential effects:</strong> A triple bogey often leads to another poor hole</li>
            </ul>

            <ArticleParagraph
                lines={[
                    "Despite these limitations, the model produces realistic distributions that match published statistics for amateur golfers. It's useful for understanding how handicap, course difficulty, and consistency interact to determine scoring outcomes."
                ]}
            />

            {/* Section 10: Resources */}
            <H2Heading>Further Reading</H2Heading>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-green-700 mb-2">Official USGA Resources</h4>
                    <ul className="space-y-2">
                        <li>
                            <a href="https://www.usga.org/content/usga/home-page/handicapping/roh/Content/rules/6%201%20Course%20Handicap%20Calculation.htm" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                Course Handicap Calculation Rules
                            </a>
                        </li>
                        <li>
                            <a href="https://www.usga.org/content/usga/home-page/course-handicap-calculator.html" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                USGA Course Handicap Calculator
                            </a>
                        </li>
                        <li>
                            <a href="https://www.usga.org/content/usga/home-page/handicapping/world-handicap-system/world-handicap-system-usga-golf-faqs.html" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                World Handicap System FAQs
                            </a>
                        </li>
                    </ul>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-green-700 mb-2">Scoring Statistics Research</h4>
                    <ul className="space-y-2">
                        <li>
                            <a href="https://mygolfspy.com/labs/study-overall-golfer-performance-by-handicap/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                MyGolfSpy: Performance By Handicap Study
                            </a>
                        </li>
                        <li>
                            <a href="https://golfinsideruk.com/birdies-bogey-doubles-by-handicap/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                Golf Insider: Scoring Breakdown by Handicap
                            </a>
                        </li>
                        <li>
                            <a href="https://www.arccosgolf.com/blogs/community/what-really-separates-a-5-handicap-from-a-10" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                Arccos: 5 vs 10 Handicap Analysis
                            </a>
                        </li>
                        <li>
                            <a href="https://practical-golf.com/shotscope-handicap-data" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                Shot Scope Data Analysis
                            </a>
                        </li>
                    </ul>
                </div>
            </div>

            {/* Navigation */}
            <div className="mt-8 pt-4 border-t border-gray-200">
                <Link
                    to="/sports-models/golf/round-prediction"
                    className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colours"
                >
                    Try the Golf Simulation &rarr;
                </Link>
            </div>
        </PageBody>
    );
};
